import { useState, useEffect, useCallback } from 'react';
import useUser from '@/hooks/useUser';

interface TimeBonus {
  threshold: number;
  bonus: number;
}

interface GameState {
  timeSpent: number;
  currentStreak: number;
  questionsAnswered: number;
  allCorrect: boolean;
  difficulty: string;
}

interface BonusDisplay {
  type: string;
  amount: number;
  description: string;
  icon?: string;
}

export const useGamePoints = (gameId: string) => {
  const { user } = useUser();
  const [points, setPoints] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [allCorrect, setAllCorrect] = useState(true);
  const [activeBonuses, setActiveBonuses] = useState<BonusDisplay[]>([]);
  const [gameConfig, setGameConfig] = useState<any>(null);
  const [timeStarted, setTimeStarted] = useState<number | null>(null);
  const [gameStartTime, setGameStartTime] = useState<number | null>(null);
  const [cerebrixEarned, setCerebrixEarned] = useState(0);

  // Cargar configuración del juego
  useEffect(() => {
    const loadGameConfig = async () => {
      try {
        const response = await fetch(`/api/games/${gameId}/config`);
        const config = await response.json();
        setGameConfig(config);
      } catch (error) {
        console.error('Error loading game config:', error);
      }
    };
    loadGameConfig();
  }, [gameId]);

  const getSpeedBonus = useCallback((timeSpent: number): number => {
    if (!gameConfig) return 0;
    const speedBonuses = gameConfig.points_per_action.speed_bonus;
    
    if (timeSpent < 5) return speedBonuses.fast;
    if (timeSpent < 10) return speedBonuses.medium;
    if (timeSpent < 15) return speedBonuses.slow;
    return 0;
  }, [gameConfig]);

  const getStreakBonus = useCallback((streak: number): number => {
    if (!gameConfig) return 0;
    const streakBonuses = gameConfig.points_per_action.streak_bonus;
    
    if (streak >= 10) return streakBonuses['10_streak'];
    if (streak >= 5) return streakBonuses['5_streak'];
    if (streak >= 3) return streakBonuses['3_streak'];
    return 0;
  }, [gameConfig]);

  const getPerfectRoundBonus = useCallback((questions: number, perfect: boolean): number => {
    if (!gameConfig || !perfect) return 0;
    const perfectBonuses = gameConfig.points_per_action.perfect_round;
    
    if (questions >= 10) return perfectBonuses['10_questions'];
    if (questions >= 5) return perfectBonuses['5_questions'];
    return 0;
  }, [gameConfig]);

  const startQuestion = useCallback(() => {
    setTimeStarted(Date.now());
    if (!gameStartTime) {
      setGameStartTime(Date.now());
    }
  }, [gameStartTime]);

  const addPoints = useCallback((correct: boolean) => {
    if (!gameConfig || !timeStarted) return;

    const timeSpent = (Date.now() - timeStarted) / 1000;
    const newBonuses: BonusDisplay[] = [];
    let totalPoints = 0;

    if (correct) {
      // Puntos base por respuesta correcta
      totalPoints += gameConfig.points_per_action.correct_answer;
      newBonuses.push({
        type: 'base',
        amount: gameConfig.points_per_action.correct_answer,
        description: '¡Respuesta Correcta!',
        icon: '✓'
      });

      // Bonus por velocidad
      const speedBonus = getSpeedBonus(timeSpent);
      if (speedBonus > 0) {
        totalPoints += speedBonus;
        newBonuses.push({
          type: 'speed',
          amount: speedBonus,
          description: '¡Respuesta Rápida!',
          icon: '⚡'
        });
      }

      // Actualizar racha
      const newStreak = currentStreak + 1;
      setCurrentStreak(newStreak);
      
      // Bonus por racha
      const streakBonus = getStreakBonus(newStreak);
      if (streakBonus > 0) {
        totalPoints += streakBonus;
        newBonuses.push({
          type: 'streak',
          amount: streakBonus,
          description: `¡Racha de ${newStreak}!`,
          icon: '🔥'
        });
      }
    } else {
      setCurrentStreak(0);
      setAllCorrect(false);
    }

    // Actualizar preguntas respondidas
    const newQuestionsAnswered = questionsAnswered + 1;
    setQuestionsAnswered(newQuestionsAnswered);

    // Bonus por ronda perfecta
    const perfectBonus = getPerfectRoundBonus(newQuestionsAnswered, allCorrect);
    if (perfectBonus > 0) {
      totalPoints += perfectBonus;
      newBonuses.push({
        type: 'perfect',
        amount: perfectBonus,
        description: '¡Ronda Perfecta!',
        icon: '🌟'
      });
    }

    // Aplicar multiplicador de dificultad
    const difficultyMultiplier = gameConfig.points_per_action.difficulty_multiplier[gameConfig.difficulty_level];
    totalPoints = Math.floor(totalPoints * difficultyMultiplier);

    setPoints(prev => prev + totalPoints);
    setActiveBonuses(newBonuses);
    setTimeStarted(null);
  }, [gameConfig, timeStarted, currentStreak, questionsAnswered, allCorrect, getSpeedBonus, getStreakBonus, getPerfectRoundBonus]);

  const finishGame = useCallback(async () => {
    if (!gameConfig || !gameStartTime || !user) return;

    const timeSpent = Math.floor((Date.now() - gameStartTime) / 1000);
    
    try {
      const response = await fetch(`/api/games/${gameId}/finish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          points,
          streak: currentStreak,
          perfectRounds: allCorrect ? questionsAnswered : 0,
          timeSpent,
          difficulty: gameConfig.difficulty_level
        }),
      });

      if (!response.ok) {
        throw new Error('Error finishing game');
      }

      const data = await response.json();
      setCerebrixEarned(data.cerebrixEarned);
      return data.cerebrixEarned;
    } catch (error) {
      console.error('Error finishing game:', error);
      return 0;
    }
  }, [gameId, points, currentStreak, questionsAnswered, allCorrect, gameStartTime, gameConfig, user]);

  const resetGame = useCallback(() => {
    setPoints(0);
    setCurrentStreak(0);
    setQuestionsAnswered(0);
    setAllCorrect(true);
    setActiveBonuses([]);
    setTimeStarted(null);
    setGameStartTime(null);
    setCerebrixEarned(0);
  }, []);

  return {
    points,
    currentStreak,
    questionsAnswered,
    activeBonuses,
    cerebrixEarned,
    startQuestion,
    addPoints,
    finishGame,
    resetGame
  };
};
